import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import ScopedAdminPage from "../components/ScopedAdminPage.jsx";
import { modeForView } from "../components/AdminMenu.jsx";
import { openAiIntegrationEnabled } from "../lib/featureFlags.js";
import HealthView from "./dataSources/HealthView.jsx";
import KeySourcePage from "./dataSources/KeySourcePage.jsx";
import PerplexityPage from "./dataSources/PerplexityPage.jsx";
import LangSmithPage from "./dataSources/LangSmithPage.jsx";
import OpenAiPage from "./dataSources/OpenAiPage.jsx";
import CopilotPage from "./dataSources/CopilotPage.jsx";
import VertexPage from "./dataSources/VertexPage.jsx";
import LiteLlmPage from "./dataSources/LiteLlmPage.jsx";
import MicrosoftFoundryPage from "./dataSources/MicrosoftFoundryPage.jsx";
import { SOURCES, sourceByKey, sourceHealth } from "./dataSources/model.js";
import { useDataSources } from "./dataSources/useDataSources.js";

// Which source page is open, and which workspace or directory within it, ride
// the URL so a source page can be linked to and Back returns to the cards.
const SOURCE_PARAM = "dataSource";
const ITEM_PARAM = "dataSourceItem";

export default function DataSourcesPage({ viewDef }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const sources = useMemo(
        () => SOURCES.filter((source) => source.key !== "openai" || openAiIntegrationEnabled()),
        [],
    );
    const keys = useMemo(() => sources.map((source) => source.key), [sources]);
    const { entries, directory, reload, replaceData } = useDataSources(keys);

    const openKey = searchParams.get(SOURCE_PARAM);
    const open = openKey && keys.includes(openKey) ? sourceByKey(openKey) : null;
    const selectedItem = searchParams.get(ITEM_PARAM);

    const navigate = useCallback(
        (sourceKey, item = null) => {
            setSearchParams((current) => {
                const next = new URLSearchParams(current);
                if (sourceKey) next.set(SOURCE_PARAM, sourceKey);
                else next.delete(SOURCE_PARAM);
                if (item) next.set(ITEM_PARAM, item);
                else next.delete(ITEM_PARAM);
                return next;
            });
        },
        [setSearchParams],
    );

    const selectItem = useCallback(
        (item) => {
            setSearchParams(
                (current) => {
                    const next = new URLSearchParams(current);
                    if (item) next.set(ITEM_PARAM, item);
                    else next.delete(ITEM_PARAM);
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const openIdentity = useCallback(() => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            next.delete(SOURCE_PARAM);
            next.delete(ITEM_PARAM);
            next.set("mode", modeForView("directory"));
            next.set("adminView", "directory");
            return next;
        });
    }, [setSearchParams]);

    const back = useCallback(() => navigate(null), [navigate]);

    let body;
    if (open) {
        const entry = entries[open.key];
        const common = {
            source: open,
            health: sourceHealth(open.key, entry, directory),
            entry,
            directory,
            onBack: back,
            onChanged: () => reload(open.key),
        };
        switch (open.key) {
            case "anthropic":
                body = <KeySourcePage key={open.key} {...common} />;
                break;
            case "openai":
                body = <OpenAiPage {...common} selectedItem={selectedItem} onSelectItem={selectItem} />;
                break;
            case "perplexity":
                body = <PerplexityPage {...common} onReplace={(data) => replaceData("perplexity", data)} />;
                break;
            case "langsmith":
                body = <LangSmithPage {...common} selectedItem={selectedItem} onSelectItem={selectItem} />;
                break;
            case "copilot":
                body = (
                    <CopilotPage
                        {...common}
                        selectedItem={selectedItem}
                        onSelectItem={selectItem}
                        onOpenIdentity={openIdentity}
                    />
                );
                break;
            case "vertex":
                body = <VertexPage {...common} selectedItem={selectedItem} onSelectItem={selectItem} />;
                break;
            case "litellm":
                body = <LiteLlmPage {...common} selectedItem={selectedItem} onSelectItem={selectItem} />;
                break;
            case "foundry":
                body = (
                    <MicrosoftFoundryPage
                        {...common}
                        selectedItem={selectedItem}
                        onSelectItem={selectItem}
                        onOpenIdentity={openIdentity}
                    />
                );
                break;
            default:
                body = null;
        }
    } else {
        body = (
            <HealthView
                sources={sources}
                entries={entries}
                directory={directory}
                onOpen={(key) => navigate(key)}
                onRetry={reload}
            />
        );
    }

    return (
        <ScopedAdminPage
            title={viewDef?.label ?? "Data Sources"}
            scope="Organization-wide"
        >
            {body}
        </ScopedAdminPage>
    );
}
